import type { ReactNode } from "react";
import { Database, X } from "lucide-react";
import type { Connector } from "@/types/connector.types";
import type { CatalogFieldDef } from "./catalog-step-config-utils";

interface ValueFieldProps {
  field: CatalogFieldDef;
  value: unknown;
  setValue: (key: string, value: unknown) => void;
}

export function UuidListField({ field, value, setValue }: ValueFieldProps) {
  return (
    <FieldShell label={field.label}>
      <textarea
        aria-label={field.label}
        value={toStringArray(value).join("\n")}
        onChange={(event) => setValue(field.key, splitIds(event.target.value))}
        rows={3}
        placeholder="Paste one ID per line..."
        className="min-h-24 w-full resize-y rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm focus:border-[var(--ring)] focus:outline-none"
      />
    </FieldShell>
  );
}

export function FieldShell({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[var(--foreground)]">{label}</label>
      {hint && <p className="-mt-1 mb-1.5 text-xs text-[var(--muted-foreground)]">{hint}</p>}
      {children}
    </div>
  );
}

export function ConnectorOption({ connector }: { connector: Connector }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Database className="size-3.5 shrink-0 text-[var(--muted-foreground)]" />
      <span className="truncate text-sm">{connector.name}</span>
      {connector.subtype && (
        <span className="shrink-0 text-xs text-[var(--muted-foreground)]">
          {connector.subtype}
        </span>
      )}
    </div>
  );
}

export function SelectedChip({
  icon,
  label,
  onRemove,
}: {
  icon: ReactNode;
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="mt-2 inline-flex max-w-full items-center gap-1 rounded-full bg-[var(--muted)] px-2 py-0.5 text-xs font-medium">
      <span className="shrink-0 text-[var(--muted-foreground)]">{icon}</span>
      <span className="truncate">{label}</span>
      <button type="button" onClick={onRemove} className="shrink-0" aria-label={`Remove ${label}`}>
        <X className="size-3" />
      </button>
    </span>
  );
}

export function ComboboxMessage({ children }: { children: ReactNode }) {
  return <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">{children}</div>;
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function splitIds(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}
